/* eslint-disable max-lines-per-function */
/* eslint-disable max-len */
import React from "react";
import { Grid, Paper, Typography, Button, ButtonGroup, Box, Chip, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
// No internal fetching; plans are provided by parent
import { RichTextReadOnly } from "mui-tiptap";
import { StarterKit } from "@tiptap/starter-kit";
import NoPlansAvailable from "./no-plans-available";
import { shouldShowNoPlans } from "./plan-selection-state";
import type { CommonStepProps } from "../step-props";

type PlanCardProps = {
  description: string;
  title: string;
  price: string;
  priceSubtext?: string;
  details: string[];
  onChoose: () => void;
  badge?: string;
  billingCycle: "annually" | "quarterly";
}

type BadgePillProps = {
  label: string;
  outlined?: boolean;
}

const BadgePill: React.FC<BadgePillProps> = ({ label, outlined }) => (
  <Box position="absolute" top={-12} left="50%" zIndex={2} style={{ transform: "translateX(-50%)" }}>
    <Chip
      size="medium"
      color={outlined ? "default" : "primary"}
      variant={outlined ? "outlined" : "filled"}
      label={label}
      style={outlined ? {} : { color: "#fff" }}
    />
  </Box>
);

const CHECK_MARKER_SIZE = 20; // Match MUI SvgIcon rendered size (small = 20px, medium = 24px; use 24 for visual parity)
const checkMarkerSvgDataUrl = (fillColor: string): string => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${CHECK_MARKER_SIZE}" height="${CHECK_MARKER_SIZE}"><path fill="${fillColor}" d="M9 16.17 4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
};

const PlanCard: React.FC<PlanCardProps> = ({
  title,
  price,
  priceSubtext,
  details,
  onChoose,
  badge,
  billingCycle,
  description,
}) => {
  const theme = useTheme();
  const style = {
    paddingTop: 0,
    paddingLeft: 3,
    "& ul": {
      paddingLeft: 3,
    },
    "& li": {
      listStyleImage: checkMarkerSvgDataUrl(theme.palette.primary.main),
    },
    "& li::marker": {
      fontSize: `${CHECK_MARKER_SIZE}px`,
      paddingRight: 3,
    },
  };
  return (
    <Box position="relative" width="1">
      {badge && (
        <BadgePill label={badge} outlined={badge === "Legacy Customer"} />
      )}
      <Box component={Paper} p={3} pt={4} height="1" display="flex" flexDirection="column">
        <Typography variant="h6" gutterBottom>{title}</Typography>
        <Typography variant="h5" color="primary" gutterBottom>{price}</Typography>
        {priceSubtext && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: -0.5, mb: 0.5 }}>
            {priceSubtext}
          </Typography>
        )}
        <Typography variant="body2" gutterBottom>
          Billed <strong>{billingCycle}</strong>
        </Typography>
        <Box flexGrow={1} mb={1}>
          <RichTextReadOnly
            content={description.replace("<p><\\p>", "")}
            extensions={[StarterKit]}
            sx={{ ...style, paddingLeft: 0 }}
          />
          <Box component="ul" sx={style}>
            {details.map((detail) => (
              <li key={detail}>{detail}</li>
            ))}
          </Box>
        </Box>
        <Button variant="contained" color="primary" fullWidth onClick={onChoose}>
          Choose Plan
        </Button>
      </Box>
    </Box>
  );
};

const PlanCardSkeleton: React.FC = () => (
  <Box position="relative" width="1">
    <Box component={Paper} p={3} pt={4} height="1" display="flex" flexDirection="column">
      <Skeleton variant="text" width="60%" height={28} />
      <Skeleton variant="text" width="40%" height={32} />
      <Skeleton variant="text" width="50%" height={20} />
      <Box flexGrow={1} mt={1}>
        <Skeleton variant="rectangular" height={14} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={14} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={14} sx={{ mb: 1 }} />
      </Box>
      <Skeleton variant="rectangular" height={36} />
    </Box>
  </Box>
);

type BillingToggleProps = {
  billingCycle: "annually" | "quarterly";
  setBillingCycle: (cycle: "annually" | "quarterly") => void;
}

const BillingToggle: React.FC<BillingToggleProps> = ({ billingCycle, setBillingCycle }) => (
  <Box display="flex" justifyContent="center" mb={4}>
    <ButtonGroup color="primary">
      <Button
        variant={billingCycle === "annually" ? "contained" : "outlined"}
        onClick={() => setBillingCycle("annually")}
      >
        Annual
      </Button>
      <Button
        variant={billingCycle === "quarterly" ? "contained" : "outlined"}
        onClick={() => setBillingCycle("quarterly")}
      >
        Quarterly
      </Button>
    </ButtonGroup>
  </Box>
);

type PlanConfig = CommonStepProps["plans"][number];

type PlanCardsGridProps = {
  billingCycle: "annually" | "quarterly";
  onChoose: (planId: string) => void;
  plans: PlanConfig[];
}

const PlanCardsGrid: React.FC<PlanCardsGridProps> = ({ billingCycle, onChoose, plans }) => (
  <Grid container spacing={2} alignItems="stretch">
    {plans.map((plan) => (
      <Grid key={plan.key} size={{ xs: 12, md: 4 }}>
        <Box height="1" display="flex">
          <PlanCard
            billingCycle={billingCycle}
            title={plan.title}
            price={plan.prices[billingCycle]}
            priceSubtext={plan.priceSubtext}
            description={plan.description}
            details={plan.details}
            onChoose={() => onChoose(plan.key)}
            badge={plan.badge}
          />
        </Box>
      </Grid>
    ))}
  </Grid>
);
type PlanSelectionProps = Pick<CommonStepProps, "onNext" | "formik" | "plans" | "plansLoading" | "plansError">;
const PlanSelection: React.FC<PlanSelectionProps> = ({ onNext, formik, plans, plansLoading, plansError }) => {

  const [billingCycle, setBillingCycle] = React.useState<"annually" | "quarterly">(
    (formik?.values?.billingCycle as "annually" | "quarterly") || "annually",
  );

  const handleSetBillingCycle = React.useCallback((cycle: "annually" | "quarterly") => {
    setBillingCycle(cycle);
    if (formik?.setFieldValue) {
      formik.setFieldValue("billingCycle", cycle);
    }
  }, [formik]);

  if (shouldShowNoPlans(plansLoading, plansError, plans.length)) {
    return <NoPlansAvailable />;
  }

  return (
    <>
      <BillingToggle billingCycle={billingCycle} setBillingCycle={handleSetBillingCycle} />
      {plansLoading && (
        <Grid container spacing={2} alignItems="stretch">
          {[0, 1, 2].map((i) => (
            <Grid key={i} size={{ xs: 12, md: 4 }}>
              <Box height="1" display="flex">
                <PlanCardSkeleton />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
      {plansError && <Typography variant="body2" color="error">Failed to load plans</Typography>}
      {!plansLoading && !plansError && (
        <PlanCardsGrid
          billingCycle={billingCycle}
          onChoose={(planId) => {
            if (formik?.setFieldValue) {
              formik.setFieldValue("planId", planId);
            }
            onNext();
          }}
          plans={plans}
        />
      )}
    </>
  );
};
export default PlanSelection;
